import axios from 'axios';
import { authService } from './authService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

class DictionaryService {
  private getAuthHeaders() {
    const token = authService.getToken();
    if (!token) {
      console.warn('No auth token available for API request');
    }
    return token ? { 'x-access-token': token } : {};
  }

  async getDictionaries(userId?: string) {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await axios.get(`${API_URL}/api/dictionaries`, {
        headers: this.getAuthHeaders(),
        params
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to get dictionaries:', error);
      throw error;
    }
  }

  async getDictionary(dictionaryName: string, userId?: string) {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await axios.get(
        `${API_URL}/api/dictionaries/${dictionaryName}`,
        {
          headers: this.getAuthHeaders(),
          params
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get dictionary:', error);
      throw error;
    }
  }

  async getDictionaryFull(dictionaryName: string, userId?: string) {
    try {
      // For full dictionary with terms, we'll need a different endpoint
      // For now, we'll use the same endpoint but the backend should return full data
      const params = userId ? { user_id: userId, full: 'true' } : { full: 'true' };
      const response = await axios.get(
        `${API_URL}/api/dictionaries/${dictionaryName}`,
        {
          headers: this.getAuthHeaders(),
          params
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get full dictionary:', error);
      throw error;
    }
  }

  async saveDictionary(
    dictionaryName: string,
    config: any,
    userId?: string
  ) {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await axios.post(
        `${API_URL}/api/dictionaries/${dictionaryName}`,
        config,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          params
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to save dictionary:', error);
      throw error;
    }
  }

  async deleteDictionary(dictionaryName: string, userId?: string) {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await axios.delete(
        `${API_URL}/api/dictionaries/${dictionaryName}`,
        {
          headers: this.getAuthHeaders(),
          params
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to delete dictionary:', error);
      throw error;
    }
  }

  async reloadConfiguration(_userId?: string) {
    // Reload is handled automatically by the API after save/delete
    // No need to call it separately
    return { status: 'success', message: 'Configuration will be reloaded automatically' };
  }

  async getEntityConfig() {
    // Entity config should come from main API, not text-analyzer
    // For now, return empty - this endpoint may need to be added to main API
    throw new Error('Entity config endpoint not yet implemented in main API');
  }
}

export const dictionaryService = new DictionaryService();

